import { Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { TitleBanner } from '@shared/ui/title-banner/title-banner';
import { FormControl, FormGroup } from '@angular/forms';
import { NavBarMenu } from '@shared/components/nav-bar-menu/nav-bar-menu';
import { MenuCard } from '@shared/interfaces/menu.interface';
import { Router, NavigationEnd, RouterOutlet, ActivatedRoute } from '@angular/router';
import { filter, map, startWith } from 'rxjs/operators';
import { of } from 'rxjs';
import { ROUTES } from '@shared/constants/routes';
import { EmployerService } from '@shared/services/employer.service';
import { AuthService } from '@shared/services/auth.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-candidate-dashboard',
  imports: [TitleBanner, NavBarMenu, RouterOutlet],
  templateUrl: './dashboard.html',
  styleUrls: [],
})
export class CandidateDashboardComponent implements OnInit {
  fromEmployerView = input(false);
  employeeId = input('');
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly employerService = inject(EmployerService);
  private readonly translateService = inject(TranslateService);

  private readonly url = toSignal(this.router.events.pipe(
    filter((event) => event instanceof NavigationEnd),
    map((event) => event.urlAfterRedirects),
    startWith(this.router.url)
  ));

  cardView = computed(() => {
    const url = this.url();
    if (!url) return 'default';
    const baseDashboard = '/private/candidate/dashboard';
    return (url !== baseDashboard && url.startsWith(baseDashboard)) ? 'small' : 'default';
  });

  private readonly destroyRef = inject(DestroyRef);
  type = {
    name: 'employer',
    label: '',
    options: [
      { value: 'Semiware S.r.l.', label: 'Semiware S.r.l.' },
      { value: 'S.p.a.', label: 'S.p.a.' },
      { value: 'S.r.l.', label: 'S.r.l.' },
    ],
  };
  form = new FormGroup({
    type: new FormControl('Semiware S.r.l.'),
  });
  selectionCards = signal<MenuCard[]>([]);
  userId = signal<string | null>(null);

  employeeResource = rxResource({
    params: () => (this.fromEmployerView() && this.employeeId()) ? { userId: this.userId(), employeeId: this.employeeId() } : null,
    stream: (params) => {
      if (!params.params?.userId || !params.params?.employeeId) return of(null);
      return this.employerService.getEmployeeDetail(params.params.userId, params.params.employeeId);
    }
  });

  employee = computed(() => {
    const res = this.employeeResource.value();
    if (res?.success) {
      const data = res.data.filter((d: any) => {
        return String(d.user.id) === String(this.employeeId())
      }) ?? [];
      return data.length > 0 ? data[0] : null;
    }
    return null;
  });

  titleBanner = computed(() => {
    const emp = this.employee();
    if (emp) {
      return emp.user.name + ' ' + emp.user.surname;
    }
    return "DASHBOARD";
  });

  hasLoaded = computed(() => !this.employeeResource.isLoading());

  constructor() {}

  ngOnInit(): void {
    this.userId.set(this.authService.user()?.user?.id ?? null);
    this.selectionCards.set(this.getCardList());
  }

  onSelectedRoutes(event: { link: string, title: string }): void {
    this.router.navigate([event.link]);
  }

  getLink(route: string) {
    if (this.fromEmployerView()) {
      const tree = this.router.createUrlTree(
        [route],
        { relativeTo: this.activatedRoute }
      );
      return this.router.serializeUrl(tree);
    }
    return `/${ROUTES.CANDIDATE_LOGGED_HOME}/${ROUTES.DASHBOARD}/${route}`
  }

  getCardList(): MenuCard[] {
    const userRole = this.authService.getRole();

    if (userRole === 'employer') {
      return [
        {
          img: '/assets/image/svg/item-person.svg',
          title: 'home.private.candidate.dashboard.cards.payslip_management.title',
          description: 'home.private.candidate.dashboard.cards.payslip_management.description',
          link: this.getLink(ROUTES.PAYSLIPS_MANAGEMENT),
          size: 'default',
        },
        {
          img: '/assets/image/svg/item-time-tracking.svg',
          title: 'home.private.candidate.dashboard.cards.time_tracking.title',
          description: 'home.private.candidate.dashboard.cards.time_tracking.description',
          link: this.getLink(ROUTES.TIME_TRACKING_MANAGEMENT),
          size: 'default',
        },
        {
          img: '/assets/image/svg/item-sheet.svg',
          title: 'home.private.employer.dashboard.cards.attendance_sheet.title',
          description: 'home.private.employer.dashboard.cards.attendance_sheet.description',
          link: this.getLink(ROUTES.ATTENDANCE_SHEET),
          size: 'default',
        },
        {
          img: '/assets/image/svg/item-contract.svg',
          title: 'home.private.candidate.dashboard.cards.contract_management.title',
          description: 'home.private.candidate.dashboard.cards.contract_management.description',
          link: this.getLink(ROUTES.CONTRACT_MANAGEMENT),
          size: 'default',
        },
      ];
    }

    // Per i candidati, restituisci tutte le card
    return [
      {
        img: '/assets/image/svg/item-person.svg',
        title: 'home.private.candidate.dashboard.cards.payslip_management.title',
        description: 'home.private.candidate.dashboard.cards.payslip_management.description',
        link: this.getLink(ROUTES.PAYSLIPS_MANAGEMENT),
        size: 'default',
      },
      {
        img: '/assets/image/svg/item-time-tracking.svg',
        title: 'home.private.candidate.dashboard.cards.time_tracking.title',
        description: 'home.private.candidate.dashboard.cards.time_tracking.description',
        link: this.getLink(ROUTES.TIME_TRACKING_MANAGEMENT),
        size: 'default',
      },
      {
        img: '/assets/image/svg/item-archived-documents.svg',
        title: 'home.private.candidate.dashboard.cards.archived_documents.title',
        description: 'home.private.candidate.dashboard.cards.archived_documents.description',
        link: this.getLink(ROUTES.ARCHIVED_DOCUMENTS),
        size: 'default',
      },
      {
        img: '/assets/image/svg/item-headphones.svg',
        title: 'home.private.employer.dashboard.cards.dispute_center.title',
        description: 'home.private.employer.dashboard.cards.dispute_center.description',
        link: this.getLink(ROUTES.DISPUTE_CENTER),
        size: 'default',
      },
      {
        img: '/assets/image/svg/item-headphones.svg',
        title: 'home.private.candidate.dashboard.cards.whistleblowing.title',
        description: 'home.private.candidate.dashboard.cards.whistleblowing.description',
        link: this.getLink(ROUTES.WHISTLEBLOWING),
        size: 'default',
      },
      {
        img: '/assets/image/svg/item-bank.svg',
        title: 'home.private.candidate.dashboard.cards.refound.title',
        description: 'home.private.candidate.dashboard.cards.refound.description',
        link: this.getLink(ROUTES.REFOUND),
        size: 'default',
      },
    ];
  }
}
